import React, { useEffect, useState, useMemo } from "react";
import Table from "../../../components/table/table";
import ServiceColumns from "./service-columns";
import { useServices } from "../../../hooks/useServices";
import {
  servicesCollection,
  rateCardsCollection,
} from "../../../services/firebase";
import { ServiceTableStyled } from "./service-table.styles";
import CreateServiceGroup from "./create-service/create-service";
import { Button } from "../../../components/button/button";
import { EncircledPlusIcon } from "../../../utils/svg-components";
import { getFirstWord } from "../../../utils/utils";

export default function ServicesTable() {
  const { data: services = [], isLoading, refetch } = useServices();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedMap, setExpandedMap] = useState({});

  const sortedServices = useMemo(() => {
    return [...services].sort((a, b) =>
      getFirstWord(a.name).localeCompare(getFirstWord(b.name))
    );
  }, [services]);

  // once services arrive, open the very first one by default
  useEffect(() => {
    if (sortedServices.length > 0 && Object.keys(expandedMap).length === 0) {
      setExpandedMap({ [sortedServices[0].id]: true });
    }
  }, [sortedServices, expandedMap]);

  // toggle a service open/closed
  function toggleExpand(id) {
    setExpandedMap((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  // delete handler
  async function handleDelete(row) {
    // 0) First—run your “not-allowed” checks
    if (row.isParent) {
      // Service group: see if any rate card is using it
      const snapshot = await rateCardsCollection.get();
      const inUse = snapshot.docs.some((doc) => {
        const groups = doc.data().serviceGroups || [];
        return groups.some((g) => String(g.id) === String(row.id));
      });
      if (inUse) {
        alert(
          `Cannot delete service group "${row.name}" because it's in use by a rate card.`
        );
        return;
      }
    } else {
      // Role: make sure it's not the last one in its group
      const parent = services.find(
        (svc) => String(svc.id) === String(row.parentId)
      );
      if (!parent) {
        console.error("Missing parent for role delete:", row);
        return;
      }
      if (parent.roles.length <= 1) {
        alert(
          `Cannot delete the last role "${row.name}" in service group "${parent.name}".`
        );
        return;
      }
    }

    // 1) Now ask confirmation
    if (!window.confirm(`Delete "${row.name}"?`)) return;

    // 2) Perform the delete or update
    if (row.isParent) {
      await servicesCollection.doc(String(row.id)).delete();
    } else {
      const parent = services.find(
        (svc) => String(svc.id) === String(row.parentId)
      );
      const updated = parent.roles.filter(
        (r) => String(r.id) !== String(row.id)
      );
      await servicesCollection
        .doc(String(row.parentId))
        .update({ roles: updated });
    }

    // 3) Refresh your list
    refetch();
  }

  const openNew = () => {
    setEditing(null);
    setShowModal(true);
  };
  const openEdit = (row) => {
    alert(
      "Are you sure? Modifying the service group or role name will update all instances throughout the system"
    );
    const parent = row.isParent
      ? row
      : services.find((svc) => String(svc.id) === String(row.parentId));

    if (!parent) {
      console.error("Parent service group not found for row:", row);
      return;
    }

    setEditing(parent);
    setShowModal(true);
  };

  const closeModal = () => {
    setEditing(null);
    setShowModal(false);
  };

  // flatten into [ parentRow, ...childRows ] depending on expandedMap
  const flattened = useMemo(() => {
    const out = [];
    for (let svc of sortedServices) {
      out.push({
        id: svc.id,
        name: svc.name,
        revenueAccount: svc.revenueAccount,
        isParent: true,
        parentId: svc.id,
        roles: svc.roles,
      });
      if (expandedMap[svc.id]) {
        const sortedRoles = [...svc.roles].sort((a, b) =>
          getFirstWord(a.name).localeCompare(getFirstWord(b.name))
        );
        for (let role of sortedRoles) {
          out.push({
            id: role.id,
            name: role.name,
            rate: role.rate,
            isParent: false,
            parentId: svc.id,
          });
        }
      }
    }
    return out;
  }, [sortedServices, expandedMap]);

  // build columns
  const columns = ServiceColumns(
    toggleExpand,
    expandedMap,
    handleDelete,
    openEdit
  );

  return (
    <ServiceTableStyled>
      <div className="buttonContainer">
        <Button
          variant="filled"
          color="tertiary"
          startIcon={<EncircledPlusIcon />}
          onClick={openNew}
        >
          Add Service Group
        </Button>
      </div>
      <Table
        data={flattened}
        columns={columns}
        isLoading={isLoading}
        noDataMessage="No services found."
        stickyHeader
      />

      {showModal && (
        <CreateServiceGroup
          initialData={editing}
          onClose={closeModal}
          onSuccess={() => {
            refetch();
            closeModal();
          }}
        />
      )}
    </ServiceTableStyled>
  );
}
